import { useCallback, useState } from 'react';
import { useMutation, useQueryClient } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import { toNumber } from 'lodash';

import { api } from '@/api';
import { RAW_DATE_FORMAT } from '@/common/constants';
import { API_URL } from '@/dtos';
import { useGetEnrollments } from '@/modules/employee/Employee/hooks/useGetEnrollment';
import { QUERY_KEY as POLICIES_STATUS_QUERY_KEY } from '@/modules/employee/EnrollmentDetails/queries/useGetPolicyStatuses.query';
import { GET_ORGANIZATION_LOCKS_KEY } from '@/modules/employer/queries/useGetOrgLocks.query';

import { LimitedUses } from '../enrollmentModal.types';
import { EnrollmentFormFields } from '../hooks/useEnrollmentForm';
import { DefaultCoverageTierDataFromBackend } from '../hooks/useEnrollmentSubmit';

import { useEmployeeEnrollmentParams } from './useEmployeeEnrollmentParams';

export interface NewEnrollmentPayload {
  employee_id: number;
  plan_id: number;
  enrollment_status: string;
  effective_date: string;
  election_amount: number;
  deductible_met?: boolean;
  external_deductible_met_date?: string | null;
}

export const useCreateEnrollment = (luGpDesignationAllowed?: boolean) => {
  const queryClient = useQueryClient();
  const { employeeId, accountId } = useEmployeeEnrollmentParams();
  const { refetch } = useGetEnrollments({
    employeeId,
    accountId,
  });
  const { isLoading, mutateAsync } = useMutation((newEnrollment: NewEnrollmentPayload) =>
    api.post(API_URL.ENROLLMENTS, newEnrollment)
  );

  const [isSubmitting, setIsSubmitting] = useState(false);

  const preparedNewEnrollment = useCallback(
    (
      formValues: EnrollmentFormFields,
      defaultCoverageTierDataFromBackend?: DefaultCoverageTierDataFromBackend
    ) => {
      return {
        ...(formValues.coverageLevel || defaultCoverageTierDataFromBackend?.coverage_level
          ? {
              coverage_tier: formValues.coverageLevel || defaultCoverageTierDataFromBackend?.coverage_level,
              coverage_tier_effective_date: dayjs(
                formValues.coverageLevelEffectiveDate ||
                  defaultCoverageTierDataFromBackend?.coverage_level_effective_date
              ).format(RAW_DATE_FORMAT),
            }
          : {}),
        employee_id: Number(employeeId),
        plan_id: Number(formValues.plan),
        enrollment_status: formValues.enrollmentStatus || '',
        effective_date: dayjs(formValues.enrollmentDate || '').format(RAW_DATE_FORMAT),
        election_amount: Number(formValues.electionAmount || 0),
        ...(formValues.perPayEmployee ? { per_pay_employee: toNumber(formValues.perPayEmployee) } : {}),
        ...(formValues.perPayEmployer ? { per_pay_employer: toNumber(formValues.perPayEmployer) } : {}),
        // LU/GP designation fields are only sent when the plan allows it (luGpDesignationAllowed).
        // Otherwise the Limited Use field is hidden and none of its dependent fields are sent.
        ...(luGpDesignationAllowed
          ? {
              deductible_met_date:
                formValues.limitedUse === LimitedUses.GENERALE_PURPOSE_DEDUCTIBLE_MET &&
                formValues.deductibleMetDate
                  ? dayjs(formValues.deductibleMetDate).format(RAW_DATE_FORMAT)
                  : null,
              ...(formValues.limitedUse && formValues.limitedUse === LimitedUses.LIMITED_USE
                ? { limited_use: true }
                : {}),
              ...(formValues.limitedUse && formValues.limitedUse === LimitedUses.GENERALE_PURPOSE
                ? { limited_use: false }
                : {}),
              ...(formValues.limitedUse &&
              formValues.limitedUse === LimitedUses.GENERALE_PURPOSE_DEDUCTIBLE_MET
                ? { deductible_met: true }
                : {}),
            }
          : {}),
      };
    },
    [employeeId, luGpDesignationAllowed]
  );

  const handleSave = async (
    formValues: EnrollmentFormFields,
    defaultCoverageTierDataFromBackend?: { coverage_level?: string; coverage_level_effective_date?: string }
  ) => {
    setIsSubmitting(true);
    try {
      const preparedDataPayload = preparedNewEnrollment(formValues, defaultCoverageTierDataFromBackend);
      await mutateAsync(preparedDataPayload);
      await new Promise((resolve) => setTimeout(resolve, 3000));
      queryClient.invalidateQueries({
        queryKey: [POLICIES_STATUS_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [GET_ORGANIZATION_LOCKS_KEY],
      });
      refetch();
      await new Promise((resolve) => setTimeout(resolve, 1500));
    } finally {
      // Always clear the submitting state, even when the request fails, so the
      // submit button does not stay stuck on an infinite loader. The error is
      // re-thrown for useEnrollmentSubmit to handle (SSN message / error modal).
      setIsSubmitting(false);
    }
  };

  return {
    onSave: handleSave,
    isLoading: isLoading || isSubmitting,
  };
};
